import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  Check as CheckIcon,
  Email as EmailIcon,
  Info as InfoIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  LocationOn as LocationOnIcon,
  Refresh as RefreshIcon,
  School as SchoolIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  UnfoldMore as UnfoldMoreIcon,
} from "@mui/icons-material";

import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@api/api";

import PaginacaoLista from "../../../componentes/comuns/PaginacaoLista";

import {
  coresTextosGlobais, iconesGlobais,
  tamanhosControlesGlobais,
  tamanhosPaginasAdministrativas,
} from "../../../estilos/global";

const OPCOES_ITENS_POR_PAGINA = [5, 10, 20];

const COLUNAS_ORDENAVEIS = [
  {
    chave: "nome",
    titulo: "Escola",
    largura: "35%",
    Icone: SchoolIcon,
  },
  {
    chave: "municipio",
    titulo: "Cidade",
    largura: "15%",
    Icone: LocationOnIcon,
  },
  {
    chave: "email",
    titulo: "E-mail",
    largura: "20%",
    Icone: EmailIcon,
  },
  {
    chave: "situacao",
    titulo: "Situação",
    largura: "15%",
    Icone: InfoIcon,
  },
];

const normalizeText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .trim();

const compareText = (firstValue = "", secondValue = "") =>
  String(firstValue).localeCompare(String(secondValue), "pt-BR", {
    sensitivity: "base",
    numeric: true,
  });

// ======================================================
// TABELA DE ESCOLAS
// ======================================================

const SchoolTable = ({ schools, onProfileClick, ordem, onSort }) => (
  <TableContainer
    component={Paper}
    elevation={0}
    sx={{
      border: "1px solid var(--sear-borda, #d7e7f7)",
      borderRadius: 1.5,
      overflowX: "auto",
    }}
  >
    <Table
      aria-label="Lista de escolas"
      size="small"
      sx={{
        minWidth: 850,
        tableLayout: "fixed",
        "& .MuiTableCell-root": {
          borderColor: "var(--sear-borda, #dfe9f3)",
        },
      }}
    >
      <TableHead
        sx={{
          backgroundColor: "var(--sear-superficie-azul, #edf6fe)",
        }}
      >
        <TableRow>
          {COLUNAS_ORDENAVEIS.map(({ chave, titulo, largura, Icone }) => {
            const ativa = ordem.coluna === chave;

            return (
              <TableCell
                key={chave}
                align={chave === "situacao" ? "center" : "left"}
                sortDirection={ativa ? ordem.direcao : false}
                sx={{
                  width: largura,
                  px: 1.4,
                  py: 0.55,
                  borderRight: "1px solid var(--sear-borda, #d9e7f3)",
                }}
              >
                <TableSortLabel
                  active={ativa}
                  direction={ativa ? ordem.direcao : "asc"}
                  onClick={() => onSort(chave)}
                  IconComponent={
                    ativa
                      ? ordem.direcao === "asc"
                        ? ArrowUpwardIcon
                        : ArrowDownwardIcon
                      : UnfoldMoreIcon
                  }
                  sx={{
                    color: coresTextosGlobais.cabecalhoTabela,
                    fontSize: tamanhosPaginasAdministrativas.cabecalhoTabela,
                    fontWeight: 700,

                    "&:hover": {
                      color: "var(--sear-azul, #0767d8)",
                    },

                    "&.Mui-active": {
                      color: coresTextosGlobais.cabecalhoTabela,
                    },

                    "& .MuiTableSortLabel-icon": {
                      opacity: 1,
                      color: "var(--sear-azul, #385b90)",
                      fontSize: iconesGlobais.conteudoTabela.fontSize,
                      ml: 0.35,
                      transform: "none !important",
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.65,
                    }}
                  >
                    <Icone
                      sx={{
                        color: "var(--sear-azul, #087cf0)",
                        fontSize: iconesGlobais.tabela.fontSize,
                        flexShrink: 0,
                      }}
                    />

                    <Box
                      component="span"
                      sx={{
                        whiteSpace: "nowrap",
                      }}
                    >
                      {titulo}
                    </Box>
                  </Box>
                </TableSortLabel>
              </TableCell>
            );
          })}

          {/* AÇÕES */}
          <TableCell
            align="center"
            sx={{
              width: "15%",
              px: 1,
              py: 0.55,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.65,
                color: "var(--sear-azul, #27456f)",
              }}
            >
              <SettingsIcon
                sx={{
                  color: "var(--sear-azul, #087cf0)",
                  fontSize: iconesGlobais.tabela.fontSize,
                }}
              />

              <Typography
                component="span"
                sx={{
                  color: coresTextosGlobais.cabecalhoTabela,
                  fontSize: tamanhosPaginasAdministrativas.cabecalhoTabela,
                  fontWeight: 700,
                }}
              >
                Ações
              </Typography>
            </Box>
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {schools.map((school) => (
          <TableRow
            key={school.id}
            hover
            sx={{
              height: 38,

              "&:hover": {
                backgroundColor: "var(--sear-superficie, #fbfdff)",
              },

              "&:last-child td": {
                borderBottom: 0,
              },
            }}
          >
            {/* ESCOLA */}
            <TableCell
              sx={{
                px: 1.4,
                py: 0.35,
                borderRight: "1px solid var(--sear-borda, #e5edf5)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    backgroundColor: "var(--sear-superficie-azul, #e7f1fd)",
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <SchoolIcon
                    sx={{
                      color: "var(--sear-azul, #087cf0)",
                      fontSize: 19,
                    }}
                  />
                </Box>

                  <ButtonBase
                    type="button"
                    disableRipple
                    onClick={() => onProfileClick(school.id)}
                    sx={{
                      display: "block",
                      textAlign: "left",
                      width: "fit-content",
                      maxWidth: "100%",
                      userSelect: "text",
                      borderRadius: "3px",
                      lineHeight: "inherit",
                      "&.Mui-focusVisible": { outline: "2px solid var(--sear-borda-forte, #087cf0)", outlineOffset: 2 },
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: coresTextosGlobais.link,
                      fontSize: tamanhosPaginasAdministrativas.conteudoTabela,
                      fontWeight: 600,
                    }}
                  >
                    {school.nome}
                  </ButtonBase>
              </Box>
            </TableCell>

            {/* CIDADE */}
            <TableCell
              sx={{
                px: 1.4,
                py: 0.35,
                borderRight: "1px solid var(--sear-borda, #e5edf5)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <LocationOnIcon
                  sx={{
                    color: "var(--sear-azul, #087cf0)",
                    fontSize: iconesGlobais.conteudoTabela.fontSize,
                    flexShrink: 0,
                  }}
                />

                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: coresTextosGlobais.conteudoTabela,
                      fontSize: tamanhosPaginasAdministrativas.conteudoTabela,
                    }}
                  >
                    {school.municipio || "Não informado"}
                  </Typography>
              </Box>
            </TableCell>

            {/* E-MAIL */}
            <TableCell
              sx={{
                px: 1.4,
                py: 0.35,
                borderRight: "1px solid var(--sear-borda, #e5edf5)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  minWidth: 0,
                }}
              >
                <EmailIcon
                  sx={{
                    color: "var(--sear-azul, #087cf0)",
                    fontSize: iconesGlobais.conteudoTabela.fontSize,
                    flexShrink: 0,
                  }}
                />

                <Tooltip title={school.email || ""}>
                  <Typography
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: coresTextosGlobais.conteudoTabela,
                      fontSize: tamanhosPaginasAdministrativas.conteudoTabela,
                    }}
                  >
                    {school.email || "Não informado"}
                  </Typography>
                </Tooltip>
              </Box>
            </TableCell>

            {/* SITUAÇÃO — EXIBIÇÃO PROVISÓRIA DA ETAPA VISUAL */}
            <TableCell
              align="center"
              sx={{
                px: 1.4,
                py: 0.35,
                borderRight: "1px solid var(--sear-borda, #e5edf5)",
              }}
            >
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  color: "var(--sear-sucesso-texto, #159b79)",
                  fontSize: tamanhosPaginasAdministrativas.conteudoTabela,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "currentColor",
                    flexShrink: 0,
                  }}
                />
                Ativa
              </Box>
            </TableCell>

            {/* AÇÕES */}
            <TableCell
              align="center"
              sx={{
                px: 0.7,
                py: 0.35,
              }}
            >
              <Button
                variant="contained"
                size="small"
                onClick={() => onProfileClick(school.id)}
                sx={{
                  minHeight: 30,
                  px: 1.6,
                  borderRadius: 1,
                  textTransform: "none",
                  fontSize: tamanhosControlesGlobais.botao,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  backgroundColor: "var(--sear-acao-azul, #087cf0)",

                  "&:hover": {
                    backgroundColor: "var(--sear-acao-azul-hover, #0767d8)",
                  },
                }}
              >
                Ver perfil
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// ======================================================
// ESTADO SEM ESCOLAS CADASTRADAS
// ======================================================

const EmptyState = ({ onRetry }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "40px 20px",
      width: "100%",
      minHeight: "400px",
    }}
  >
    <motion.div
      animate={{
        scale: [1, 1.05, 1],
        rotate: [0, -3, 3, 0],
      }}
      transition={{
        duration: 1.5,
        repeat: Infinity,
        repeatType: "reverse",
      }}
    >
      <Box
        sx={{
          width: 120,
          height: 120,
          borderRadius: "50%",
          backgroundColor: "rgba(25, 118, 210, 0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
          boxShadow: "0 8px 24px rgba(25, 118, 210, 0.1)",
        }}
      >
        <SchoolIcon
          sx={{
            fontSize: 60,
            color: "primary.main",
          }}
        />
      </Box>
    </motion.div>

    <Typography
      variant="h5"
      sx={{
        fontWeight: 600,
        mb: 1.5,
        background: "linear-gradient(90deg, var(--sear-azul, #1976d2), var(--sear-azul, #2196f3))",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
      }}
    >
      Nenhuma escola encontrada
    </Typography>

    <Typography
      variant="body1"
      sx={{
        color: "text.secondary",
        mb: 4,
        maxWidth: "450px",
      }}
    >
      Não há escolas cadastradas no sistema no momento.
    </Typography>

    <Button
      variant="contained"
      onClick={onRetry}
      startIcon={<RefreshIcon />}
      sx={{
        borderRadius: "20px",
        px: 4,
        py: 1,
        fontWeight: "bold",
        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.2)",
      }}
    >
      Recarregar
    </Button>
  </div>
);

// ======================================================
// PÁGINA DE ESCOLAS CADASTRADAS
// ======================================================

export default function SchoolProfilesPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [schools, setSchools] = useState([]);

  const [searchTerm, setSearchTerm] = useState(searchParams.get("busca") || "");

  const [selectedCity, setSelectedCity] = useState(
    searchParams.get("cidade") || "",
  );

  const [cityMenuAnchor, setCityMenuAnchor] = useState(null);

  const cityMenuOpen = Boolean(cityMenuAnchor);

  const [page, setPage] = useState(() => {
    const savedPage = Number(searchParams.get("pagina"));

    return Number.isInteger(savedPage) && savedPage > 0 ? savedPage : 1;
  });

  const [itensPorPagina, setItensPorPagina] = useState(5);

  const [ordem, setOrdem] = useState({
    coluna: "nome",
    direcao: "asc",
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  // ====================================================
  // CONSULTA DE ESCOLAS
  // ====================================================

  useEffect(() => {
    fetchSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSchools = () => {
    setLoading(true);
    setError(null);

    fetch(api.allSchools(), {
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Erro ao carregar escolas");
        }

        return response.json();
      })
      .then((data) => {
        setSchools(data);
      })
      .catch((fetchError) => {
        console.error("Erro ao buscar escolas:", fetchError);

        setError(fetchError.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // ====================================================
  // PARÂMETROS DA URL
  // ====================================================

  useEffect(() => {
    const params = new URLSearchParams();

    const normalizedSearchTerm = searchTerm.trim();

    if (normalizedSearchTerm) {
      params.set("busca", normalizedSearchTerm);
    }

    if (selectedCity) {
      params.set("cidade", selectedCity);
    }

    if (page > 1) {
      params.set("pagina", String(page));
    }

    setSearchParams(params, {
      replace: true,
    });
  }, [searchTerm, selectedCity, page, setSearchParams]);

  // ====================================================
  // ORDENAÇÃO
  // ====================================================

  const sortedSchools = [...schools].sort((firstSchool, secondSchool) => {
    const resultado = compareText(
      normalizeText(firstSchool[ordem.coluna]),
      normalizeText(secondSchool[ordem.coluna]),
    );

    return ordem.direcao === "asc" ? resultado : -resultado;
  });

  // ====================================================
  // CIDADES DISPONÍVEIS
  // ====================================================

  const availableCities = Array.from(
    new Map(
      sortedSchools
        .map((school) => school.municipio?.trim())
        .filter(Boolean)
        .map((city) => [normalizeText(city), city]),
    ).values(),
  ).sort(compareText);

  const normalizedSearchTerm = normalizeText(searchTerm);

  const normalizedSelectedCity = normalizeText(selectedCity);

  // ====================================================
  // BUSCA E FILTROS
  // ====================================================

  const hasExactCity =
    normalizedSearchTerm.length >= 2 &&
    sortedSchools.some(
      (school) => normalizeText(school.municipio) === normalizedSearchTerm,
    );

  const hasCityStartingWithTerm =
    normalizedSearchTerm.length >= 2 &&
    sortedSchools.some((school) =>
      normalizeText(school.municipio).startsWith(normalizedSearchTerm),
    );

  const filteredSchools = sortedSchools.filter((school) => {
    const schoolName = normalizeText(school.nome);

    const schoolCity = normalizeText(school.municipio);

    if (normalizedSelectedCity && schoolCity !== normalizedSelectedCity) {
      return false;
    }

    if (hasExactCity) {
      return schoolCity === normalizedSearchTerm;
    }

    if (hasCityStartingWithTerm) {
      return schoolCity.startsWith(normalizedSearchTerm);
    }

    return schoolName.includes(normalizedSearchTerm);
  });

  // ====================================================
  // PAGINAÇÃO
  // ====================================================

  const totalPages = Math.max(
    1,
    Math.ceil(filteredSchools.length / itensPorPagina),
  );

  const paginaSegura = Math.min(page, totalPages);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const inicio = (paginaSegura - 1) * itensPorPagina;

  const fim = Math.min(inicio + itensPorPagina, filteredSchools.length);

  const paginatedSchools = filteredSchools.slice(inicio, fim);

  const resumoResultados = filteredSchools.length
    ? `Mostrando ${inicio + 1} a ${fim} de ${filteredSchools.length} resultados`
    : "Mostrando 0 resultados";

  // ====================================================
  // EVENTOS
  // ====================================================

  const handleProfileClick = (id) => {
    navigate(`/perfil-escola/${id}`);
  };

  const handleCityMenuOpen = (event) => {
    setCityMenuAnchor(event.currentTarget);
  };

  const handleCityMenuClose = () => {
    setCityMenuAnchor(null);
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setPage(1);
    setCityMenuAnchor(null);
  };

  const handleSort = (coluna) => {
    setOrdem((atual) => ({
      coluna,
      direcao:
        atual.coluna === coluna && atual.direcao === "asc" ? "desc" : "asc",
    }));

    setPage(1);
  };

  // ====================================================
  // INTERFACE
  // ====================================================

  return (
    <Box sx={{ width: "100%", py: 0 }}>
      {/* CABEÇALHO */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.4,
          mb: 1,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            borderRadius: 1.5,
            color: "var(--sear-azul, #087cf0)",
            backgroundColor: "var(--sear-superficie-azul, #e8f4ff)",
          }}
        >
          <SchoolIcon sx={{ fontSize: 27 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              color: coresTextosGlobais.tituloPagina,
              fontSize: tamanhosPaginasAdministrativas.tituloPagina,
              fontWeight: 700,
              lineHeight: 1.15,
            }}
          >
            Escolas Cadastradas
          </Typography>

          <Typography
            sx={{
              mt: 0.3,
              color: "var(--sear-texto-secundario, #7b8798)",
              fontSize: tamanhosControlesGlobais.descricao,
              lineHeight: 1.35,
            }}
          >
            Consulte as escolas cadastradas e seus dados institucionais.
          </Typography>
        </Box>
      </Box>

      {/* PESQUISA, FILTRO E REGISTROS */}
      <Box
        sx={{
          display: "flex",
          alignItems: {
            xs: "stretch",
            md: "center",
          },
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 1.25,
          px: 1.25,
          py: 0.55,
          mb: 0.5,
          border: "1px solid var(--sear-borda, #d5e7f7)",
          borderRadius: 1.5,
          backgroundColor: "var(--sear-superficie-azul, #f7fbff)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "stretch",
              sm: "center",
            },
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            flex: 1,
            minWidth: 0,
            gap: 1.25,
          }}
        >
          {/* CAMPO DE PESQUISA */}
          <TextField
            size="small"
            variant="outlined"
            placeholder="Buscar escola ou cidade..."
            inputProps={{
              "aria-label": "Buscar escola ou cidade",
            }}
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: "var(--sear-azul, #087cf0)",
                      fontSize: 18,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              width: {
                xs: "100%",
                sm: 440,
              },
              maxWidth: "100%",

              "& .MuiOutlinedInput-root": {
                height: 34,
                backgroundColor: "var(--sear-superficie, #ffffff)",
                borderRadius: 1,

                "& fieldset": {
                  borderColor: "var(--sear-borda, #d7e0ea)",
                },

                "&:hover fieldset": {
                  borderColor: "var(--sear-borda, #a9c9e8)",
                },
              },

              "& .MuiInputBase-input": {
                fontSize: tamanhosControlesGlobais.campoBusca,
              },
            }}
          />

          {/* FILTRO POR CIDADE */}
          <Button
            id="city-filter-button"
            size="small"
            variant={selectedCity ? "contained" : "outlined"}
            startIcon={<LocationOnIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            onClick={handleCityMenuOpen}
            aria-controls={cityMenuOpen ? "city-filter-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={cityMenuOpen ? "true" : undefined}
            sx={{
              height: 34,
              minWidth: 120,
              borderRadius: 1,
              textTransform: "none",
              fontSize: tamanhosControlesGlobais.botao,
              fontWeight: 500,
              whiteSpace: "nowrap",
            }}
          >
            {selectedCity || "Todas as Cidades"}
          </Button>

          <Menu
            id="city-filter-menu"
            anchorEl={cityMenuAnchor}
            open={cityMenuOpen}
            onClose={handleCityMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            MenuListProps={{
              "aria-labelledby": "city-filter-button",
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 240,
                maxHeight: 320,
                borderRadius: "10px",
              },
            }}
          >
            <MenuItem
              selected={!selectedCity}
              onClick={() => handleCitySelect("")}
              sx={{
                display: "flex",
                gap: 2,
              }}
            >
              <Box component="span" sx={{ flex: 1 }}>
                Todas as cidades
              </Box>

              {!selectedCity && <CheckIcon color="primary" fontSize="small" />}
            </MenuItem>

            {availableCities.map((city) => {
              const isSelected = normalizedSelectedCity === normalizeText(city);

              return (
                <MenuItem
                  key={normalizeText(city)}
                  selected={isSelected}
                  onClick={() => handleCitySelect(city)}
                  sx={{
                    display: "flex",
                    gap: 2,
                  }}
                >
                  <Box component="span" sx={{ flex: 1 }}>
                    {city}
                  </Box>

                  {isSelected && <CheckIcon color="primary" fontSize="small" />}
                </MenuItem>
              );
            })}
          </Menu>
        </Box>

        {/* SEPARADOR */}
        <Box
          sx={{
            display: {
              xs: "none",
              md: "block",
            },
            width: "1px",
            height: 30,
            flexShrink: 0,
            backgroundColor: "var(--sear-superficie-azul, #d8e7f4)",
          }}
        />

        {/* REGISTROS POR PÁGINA */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: {
              xs: "space-between",
              md: "flex-end",
            },
            gap: 1.2,
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "var(--sear-texto-secundario, #667b98)",
              fontSize: tamanhosControlesGlobais.rotulo,
              whiteSpace: "nowrap",
            }}
          >
            Registros por página:
          </Typography>

          <Select
            size="small"
            value={itensPorPagina}
            onChange={(event) => {
              setItensPorPagina(Number(event.target.value));
              setPage(1);
            }}
            renderValue={(valor) => `${valor} por página`}
            inputProps={{
              "aria-label": "Registros por página",
            }}
            sx={{
              width: 160,
              height: 34,
              backgroundColor: "var(--sear-superficie, #ffffff)",
              color: "var(--sear-azul, #314d76)",
              borderRadius: 1,
              fontSize: tamanhosControlesGlobais.seletor,
              fontWeight: 500,

              "& .MuiSelect-select": {
                py: 0.55,
              },
            }}
          >
            {OPCOES_ITENS_POR_PAGINA.map((quantidade) => (
              <MenuItem key={quantidade} value={quantidade}>
                {quantidade} por página
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {/* PAGINAÇÃO SUPERIOR */}
      {!loading && !error && filteredSchools.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: "space-between",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            gap: 0.75,
            px: 1.5,
            py: 0.35,
            mb: 0.5,
            border: "1px solid var(--sear-borda, #d7e8f8)",
            borderRadius: 1.5,
            backgroundColor: "var(--sear-superficie-azul, #f2f8fe)",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "var(--sear-texto-secundario, #536b8b)",
              fontSize: tamanhosControlesGlobais.contador,
            }}
          >
            {filteredSchools.length}{" "}
            {filteredSchools.length === 1 ? "registro" : "registros"}
          </Typography>

          <Box
            sx={{
              ml: {
                sm: "auto",
              },
            }}
          >
            <PaginacaoLista
              paginaAtual={paginaSegura}
              totalPaginas={totalPages}
              onChange={setPage}
              ariaLabel="Paginação superior da lista de escolas"
            />
          </Box>
        </Box>
      )}

      {/* CARREGAMENTO, ERRO OU TABELA */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
          }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "linear",
            }}
          >
            <CircularProgress size={60} thickness={4} />
          </motion.div>
        </Box>
      ) : error ? (
        <Paper
          elevation={3}
          sx={{
            borderRadius: "16px",
            p: 4,
            textAlign: "center",
            backgroundColor: "background.paper",
          }}
        >
          <Typography color="error" sx={{ mb: 2 }}>
            Erro: {error}
          </Typography>

          <Button
            variant="contained"
            onClick={fetchSchools}
            startIcon={<RefreshIcon />}
            sx={{
              borderRadius: "12px",
              px: 4,
              py: 1.5,
              fontWeight: "bold",
            }}
          >
            Tentar novamente
          </Button>
        </Paper>
      ) : schools.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <EmptyState onRetry={fetchSchools} />
        </Box>
      ) : filteredSchools.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "12px",
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
            }}
          >
            Nenhuma escola encontrada
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Não encontramos resultados para "{searchTerm}".
          </Typography>

          <Button
            variant="outlined"
            onClick={() => {
              setSearchTerm("");
              setPage(1);
            }}
          >
            Limpar busca
          </Button>
        </Paper>
      ) : (
        <SchoolTable
          schools={paginatedSchools}
          onProfileClick={handleProfileClick}
          ordem={ordem}
          onSort={handleSort}
        />
      )}

      {/* PAGINAÇÃO INFERIOR */}
      {!loading && !error && filteredSchools.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: "space-between",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            gap: 0.75,
            mt: 0.45,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "var(--sear-texto-secundario, #526b8d)",
              fontSize: tamanhosControlesGlobais.contador,
            }}
          >
            {resumoResultados}
          </Typography>

          <Box
            sx={{
              ml: {
                sm: "auto",
              },
            }}
          >
            <PaginacaoLista
              paginaAtual={paginaSegura}
              totalPaginas={totalPages}
              onChange={setPage}
              ariaLabel="Paginação inferior da lista de escolas"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
